import {
  Bug,
  HeartPulse,
  Loader2,
  PackageCheck,
  Pause,
  Play,
  RefreshCw,
  Rocket,
  Shuffle,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/auth/auth-provider';
import { ConfirmDialog } from '@/components/fleet/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { UseFleetAction } from '@/hooks/use-fleet-action';
import type { UseLifecycleAction } from '@/hooks/use-lifecycle-action';
import { useProvisionServer } from '@/hooks/use-provision-server';
import { LIFECYCLE_ACTIONS, type LifecycleActionDef } from '@/lib/lifecycle-actions';
import { SERVER_ACTIONS, type ServerActionDef } from '@/lib/server-actions';
import type { ServerDetail } from '@/types/fleet';

/** Icon per action key. */
const ICONS: Record<string, LucideIcon> = {
  'health-check': HeartPulse,
  'debug-tunnel': Bug,
  'check-russia': Shuffle,
  'update-images': PackageCheck,
  restart: RefreshCw,
  'rotate-awg-obfuscation': Shuffle,
};

/** Icon per destructive lifecycle action key. */
const LIFECYCLE_ICONS: Record<string, LucideIcon> = {
  quarantine: Pause,
  recover: Play,
  decommission: Trash2,
};

/**
 * Per-server operations. The six non-destructive actions are wired through the
 * Provisioning API runner (PR-E #2); the three destructive lifecycle actions
 * (decommission / quarantine / recover) hit the dedicated Fleet lifecycle
 * endpoints (PR-E #3). `support`-role users see every button disabled with a
 * tooltip — the API enforces RBAC, the UI just avoids 403s.
 */
export function ActionMenu({
  server,
  action,
  lifecycle,
}: {
  server: ServerDetail;
  action: UseFleetAction;
  lifecycle: UseLifecycleAction;
}): JSX.Element {
  const { isAdmin } = useAuth();
  const [pendingAction, setPendingAction] = useState<ServerActionDef | null>(null);
  const [pendingLifecycle, setPendingLifecycle] = useState<LifecycleActionDef | null>(null);

  const visibleActions = SERVER_ACTIONS.filter((a) => !a.visible || a.visible(server));
  const visibleLifecycle = LIFECYCLE_ACTIONS.filter((a) => a.visible(server));

  // Re-dispatch the Provision Server playbook for a stuck/failed server using
  // its stored SSH creds + recorded protocols — lets operators retry a failed
  // provision without deleting and recreating the fleet row.
  const provision = useProvisionServer();
  const [confirmProvision, setConfirmProvision] = useState(false);
  const canRetryProvision = server.status === 'provisioning';

  function disabledReason(): string | null {
    if (!isAdmin) return 'Read-only: admin role required';
    if (action.playbooksError) return 'Provisioning catalogue unavailable';
    if (!action.playbooksReady) return 'Loading playbook catalogue…';
    return null;
  }

  const blockReason = disabledReason();

  const retryButton = (
    <Button
      variant="default"
      size="sm"
      disabled={!isAdmin || provision.isPending}
      className="w-full justify-start"
      onClick={() => setConfirmProvision(true)}
    >
      {provision.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <Rocket className="h-4 w-4" aria-hidden />
      )}
      Retry provision
    </Button>
  );

  return (
    <div className="flex flex-col gap-2">
      {canRetryProvision && (
        <>
          {isAdmin ? (
            retryButton
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>{retryButton}</span>
              </TooltipTrigger>
              <TooltipContent>Read-only: admin role required</TooltipContent>
            </Tooltip>
          )}
          <div className="mb-1 border-b border-border pb-1" />
        </>
      )}

      {visibleActions.map((a) => {
        const Icon = ICONS[a.key] ?? RefreshCw;
        const unmapped = action.playbooksReady && !action.canRun(a);
        const disabled = Boolean(blockReason) || unmapped || action.isSubmitting;
        const tooltip = blockReason ?? (unmapped ? 'Playbook not in the catalogue' : null);

        const button = (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className="w-full justify-start"
            onClick={() => setPendingAction(a)}
          >
            {action.isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Icon className="h-4 w-4" aria-hidden />
            )}
            {a.label}
          </Button>
        );

        return tooltip ? (
          <Tooltip key={a.key}>
            <TooltipTrigger asChild>
              <span tabIndex={0}>{button}</span>
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        ) : (
          <div key={a.key}>{button}</div>
        );
      })}

      {/* Destructive lifecycle actions — wired in PR-E #3. */}
      {visibleLifecycle.length > 0 && <div className="mt-1 border-t border-border pt-1" />}

      {visibleLifecycle.map((a) => {
        const Icon = LIFECYCLE_ICONS[a.key] ?? Trash2;
        // recover is a benign un-quarantine — render it as a non-destructive
        // outline button; quarantine/decommission stay red.
        const isRecover = a.key === 'recover';
        const disabled = !isAdmin || lifecycle.isSubmitting;

        const button = (
          <Button
            variant={isRecover ? 'outline' : 'destructive'}
            size="sm"
            disabled={disabled}
            className="w-full justify-start"
            onClick={() => setPendingLifecycle(a)}
          >
            {lifecycle.isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Icon className="h-4 w-4" aria-hidden />
            )}
            {a.label}
          </Button>
        );

        return isAdmin ? (
          <div key={a.key}>{button}</div>
        ) : (
          <Tooltip key={a.key}>
            <TooltipTrigger asChild>
              <span tabIndex={0}>{button}</span>
            </TooltipTrigger>
            <TooltipContent>Read-only: admin role required</TooltipContent>
          </Tooltip>
        );
      })}

      <ConfirmDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
        title={pendingAction ? `${pendingAction.label} — ${server.name}` : ''}
        description={pendingAction ? pendingAction.confirmCopy(server) : ''}
        confirmLabel={pendingAction?.label ?? 'Confirm'}
        pending={action.isSubmitting}
        onConfirm={() => {
          if (pendingAction) {
            action.run(pendingAction);
            setPendingAction(null);
          }
        }}
      />

      <ConfirmDialog
        open={pendingLifecycle !== null}
        onOpenChange={(open) => {
          if (!open) setPendingLifecycle(null);
        }}
        title={pendingLifecycle ? `${pendingLifecycle.label} — ${server.name}` : ''}
        description={pendingLifecycle ? pendingLifecycle.confirmCopy(server) : ''}
        confirmLabel={pendingLifecycle?.label ?? 'Confirm'}
        variant={
          pendingLifecycle && pendingLifecycle.key !== 'recover' ? 'destructive' : 'default'
        }
        reasonRequired={pendingLifecycle?.reasonRequired ?? false}
        confirmPhrase={
          pendingLifecycle?.requireNameConfirm ? server.name : undefined
        }
        pending={lifecycle.isSubmitting}
        onConfirm={(reason) => {
          if (pendingLifecycle) {
            lifecycle.run(pendingLifecycle, reason);
            setPendingLifecycle(null);
          }
        }}
      />

      <ConfirmDialog
        open={confirmProvision}
        onOpenChange={(open) => {
          if (!open) setConfirmProvision(false);
        }}
        title={`Retry provision — ${server.name}`}
        description={`Re-dispatch the Provision Server playbook for ${server.name} using its stored SSH credentials and recorded protocols. Use this to retry a stuck or failed provision without re-entering details or deleting the server.`}
        confirmLabel="Retry provision"
        pending={provision.isPending}
        onConfirm={() => {
          provision.mutate(server.name, {
            onSuccess: ({ task_id }) =>
              toast.success(`Provisioning ${server.name} dispatched (task #${task_id}).`),
            onError: (err) => toast.error(err.message),
          });
          setConfirmProvision(false);
        }}
      />
    </div>
  );
}
